/**
 * Mağaza abonelikleri — platforma göre ödeme (istemci tarafı).
 *
 *  - iOS (Capacitor):        StoreKit 2, @capgo/native-purchases → jwsRepresentation
 *  - Android (Play TWA):     Digital Goods API + PaymentRequest('https://play.google.com/billing') → purchaseToken
 *  - Web:                    Shopier (SubscriptionModal içinde, bu servis kullanılmaz)
 * Doğrulama ve PRO yazımı her zaman sunucuda: functions/src/storeBilling.ts → verifyStorePurchase.
 * Ayrıntı: .agent/docs/magaza_abonelik_mimarisi.md
 */
import { registerPlugin } from '@capacitor/core';
import { auth } from './firebase';
import { isNativeIOS, isAndroidTwa, getScreenshotPlatform } from '../utils/capacitorBridge';
import { Sentry } from './sentry';

/** Apple product ID = Google subscription product ID (functions/src/storeBilling.ts ile aynı) */
export const MONTHLY_PRO_PRODUCT_ID = 'com.kapihoca.kpss.monthly_pro';
const PLAY_BILLING_METHOD = 'https://play.google.com/billing';
const ANDROID_PACKAGE = 'com.kapihoca.kpss';
/** uid → Apple appAccountToken (UUIDv5). Sunucudaki APP_ACCOUNT_NAMESPACE ile aynı olmalı. */
const APP_ACCOUNT_NAMESPACE = '6f1c2a4e-8b3d-5e7f-9a1b-2c3d4e5f6a7b';
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://europe-west1-tarih-atlas-kpss.cloudfunctions.net';

export type BillingPlatform = 'ios' | 'play' | 'web';

export const getBillingPlatform = (): BillingPlatform => {
    if (isNativeIOS() || getScreenshotPlatform() === 'ios') return 'ios';
    if (isAndroidTwa() && typeof window !== 'undefined' && 'getDigitalGoodsService' in window) return 'play';
    return 'web';
};

/** Mağaza uygulamasında mı? (Shopier gibi harici ödeme bağlantıları gösterilmemeli) */
export const isStoreApp = () => isNativeIOS() || isAndroidTwa() || getScreenshotPlatform() !== null;

async function uuidv5(name: string, namespace = APP_ACCOUNT_NAMESPACE): Promise<string> {
    const ns = namespace.replace(/-/g, '').match(/.{2}/g)!.map((h) => parseInt(h, 16));
    const bytes = new Uint8Array([...ns, ...new TextEncoder().encode(name)]);
    const hash = new Uint8Array(await crypto.subtle.digest('SHA-1', bytes));
    hash[6] = (hash[6] & 0x0f) | 0x50;
    hash[8] = (hash[8] & 0x3f) | 0x80;
    const hex = Array.from(hash.slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * iOS StoreKit 2 eklentisine (@capgo/native-purchases) Capacitor köprüsü üzerinden doğrudan bağlanır.
 * npm paketini import ETME: `import(değişken)` Vite'ta pakete girmez ve iOS'ta "modül bulunamadı" hatası verir;
 * statik import ise web derlemesini bozuyordu. Native taraf 'NativePurchases' adıyla kayıtlı (Package.swift, cap sync).
 */
const NativePurchasesBridge = registerPlugin<any>('NativePurchases');
const PURCHASE_TYPE = { INAPP: 'inapp', SUBS: 'subs' } as const;

async function getNativePurchases() {
    return { NativePurchases: NativePurchasesBridge, PURCHASE_TYPE };
}

/**
 * Google Play Billing bu kurulumda yok mu? Uygulama Play Store'dan değil APK/USB ile yüklendiyse ya da cihazda
 * Play Store/hesap yoksa Digital Goods API "OperationError: clientAppError" verir. Beklenen durum: Sentry'ye gönderme.
 */
const isPlayBillingUnavailable = (err: any) => /clientAppError|NotSupportedError|not supported|Billing kullanılamıyor/i.test(`${err?.name} ${err?.message || err}`);
const PLAY_UNAVAILABLE_MSG = "Google Play ödemesi bu kurulumda kullanılamıyor. Uygulamayı Google Play Store'dan yükleyip tekrar dene.";

async function getPlayService(): Promise<any> {
    const service = await (window as any).getDigitalGoodsService(PLAY_BILLING_METHOD);
    if (!service) throw new Error('Google Play Billing kullanılamıyor.');
    return service;
}

export type VerifyResult = { success: boolean; isPremium?: boolean; premiumUntil?: number | null; error?: string };

async function verifyOnServer(body: Record<string, unknown>): Promise<VerifyResult> {
    const user = auth.currentUser;
    if (!user) return { success: false, error: 'Satın alma için giriş yapmalısın.' };
    const res = await fetch(`${BACKEND_URL}/verifyStorePurchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        // Ödeme mağazada alındı ama sunucu doğrulayamadı → kullanıcı PRO olamaz; mutlaka görülmeli
        Sentry.captureMessage(`[StoreBilling] verifyStorePurchase ${res.status}: ${json.error || ''}`, { level: 'error', tags: { section: 'store_billing_verify', platform: String(body.platform) } });
        return { success: false, error: json.error || `Doğrulama başarısız (${res.status})` };
    }
    return json;
}

/** Mağazanın yerelleştirilmiş fiyatı (ör. "₺999,99"). Alınamazsa null → ekranda varsayılan fiyat gösterilir. */
export async function loadStorePrice(): Promise<string | null> {
    if (getScreenshotPlatform()) return null;
    try {
        const platform = getBillingPlatform();
        if (platform === 'ios') {
            const { NativePurchases, PURCHASE_TYPE } = await getNativePurchases();
            const { products } = await NativePurchases.getProducts({ productIdentifiers: [MONTHLY_PRO_PRODUCT_ID], productType: PURCHASE_TYPE.SUBS });
            return products[0]?.priceString || null;
        }
        if (platform === 'play') {
            const [item] = await (await getPlayService()).getDetails([MONTHLY_PRO_PRODUCT_ID]);
            if (!item?.price) return null;
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: item.price.currency }).format(Number(item.price.value));
        }
    } catch (e) {
        console.warn('[StoreBilling] Fiyat alınamadı:', e);
    }
    return null;
}

/** Aylık PRO satın alma. Kullanıcı vazgeçerse { success:false, error:'cancelled' } döner. */
export async function purchaseMonthlyPro(): Promise<VerifyResult> {
    if (getScreenshotPlatform()) return { success: false, error: 'Ekran görüntüsü modu: satın alma kapalı.' };
    const platform = getBillingPlatform();
    const user = auth.currentUser;
    if (!user) return { success: false, error: 'Satın alma için giriş yapmalısın.' };
    Sentry.addBreadcrumb({ category: 'payment', message: `Mağaza satın alma başladı (${platform})`, level: 'info' });

    try {
        if (platform === 'ios') {
            const { NativePurchases, PURCHASE_TYPE } = await getNativePurchases();
            const tx = await NativePurchases.purchaseProduct({
                productIdentifier: MONTHLY_PRO_PRODUCT_ID,
                productType: PURCHASE_TYPE.SUBS,
                appAccountToken: await uuidv5(user.uid), // Apple bildirimlerinde kullanıcıyı eşlemek için
            });
            if (!tx?.jwsRepresentation) return { success: false, error: 'Apple işlem imzası alınamadı.' };
            return await verifyOnServer({ platform: 'ios', jws: tx.jwsRepresentation });
        }

        if (platform === 'play') {
            await getPlayService(); // Play Billing yoksa erken hata
            const request = new PaymentRequest(
                [{ supportedMethods: PLAY_BILLING_METHOD, data: { sku: MONTHLY_PRO_PRODUCT_ID } }],
                // Tutarı Google Play belirler; bu alan API gereği zorunlu ama yok sayılır
                { total: { label: 'Aylık PRO', amount: { currency: 'TRY', value: '0' } } },
            );
            const response = await request.show();
            const purchaseToken = (response.details as any)?.purchaseToken;
            if (!purchaseToken) {
                await response.complete('fail');
                return { success: false, error: 'Google Play satın alma bilgisi alınamadı.' };
            }
            // Sunucu doğrular ve 3 gün kuralı için onaylar (acknowledge). Abonelikte consume() çağrılmaz.
            const result = await verifyOnServer({ platform: 'android', purchaseToken });
            await response.complete(result.success ? 'success' : 'fail');
            return result;
        }

        return { success: false, error: 'Bu cihazda mağaza ödemesi yok.' };
    } catch (err: any) {
        const msg = String(err?.message || err);
        if (err?.name === 'AbortError' || /cancel/i.test(msg) || err?.code === 'USER_CANCELLED') {
            return { success: false, error: 'cancelled' };
        }
        if (/pending|deferred/i.test(msg)) {
            // Apple "Ask to Buy" / Google bekleyen ödeme: onaylanınca bildirim gelir, PRO otomatik açılır
            return { success: false, error: 'Satın alma onay bekliyor. Onaylandığında PRO otomatik açılacak.' };
        }
        if (platform === 'play' && isPlayBillingUnavailable(err)) return { success: false, error: PLAY_UNAVAILABLE_MSG };
        console.error('[StoreBilling] Satın alma hatası:', err);
        Sentry.captureException(err, { tags: { section: 'store_billing', platform } });
        return { success: false, error: msg };
    }
}

/**
 * Mağazadaki mevcut aboneliği hesaba bağlar ("Satın Alımları Geri Yükle" + açılışta sessiz kontrol).
 * iOS: `NativePurchases.restorePurchases()` = `AppStore.sync()`, kullanıcıya Apple Kimliği girişi sordurabilir; Apple
 * yalnızca kullanıcı düğmeye basınca çağrılmasını ister. Sessiz kontrolde (silent) atlanır, cihazdaki StoreKit kayıtları
 * (`Transaction.currentEntitlements`, şifre sormaz) okunur. 2026-10-14: her gün açılışta çağrılıyor, kapatılan giriş
 * penceresi Sentry'ye "Request Canceled" olarak düşüyordu.
 */
export async function restorePurchases(opts: { silent?: boolean } = {}): Promise<VerifyResult> {
    if (getScreenshotPlatform()) return { success: false, error: 'Ekran görüntüsü modu: satın alma kapalı.' };
    const platform = getBillingPlatform();
    try {
        if (platform === 'ios') {
            const { NativePurchases, PURCHASE_TYPE } = await getNativePurchases();
            if (!opts.silent) await NativePurchases.restorePurchases();
            const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.SUBS, onlyCurrentEntitlements: true });
            const transactions = purchases.map((p) => p.jwsRepresentation).filter(Boolean);
            if (!transactions.length) return { success: false, error: 'Bu Apple hesabında aktif abonelik bulunamadı.' };
            return await verifyOnServer({ platform: 'ios', transactions });
        }
        if (platform === 'play') {
            const purchases: any[] = await (await getPlayService()).listPurchases();
            const purchaseTokens = purchases.filter((p) => p.itemId === MONTHLY_PRO_PRODUCT_ID).map((p) => p.purchaseToken);
            if (!purchaseTokens.length) return { success: false, error: 'Bu Google hesabında aktif abonelik bulunamadı.' };
            return await verifyOnServer({ platform: 'android', purchaseTokens });
        }
        return { success: false, error: 'Bu cihazda mağaza ödemesi yok.' };
    } catch (err: any) {
        console.warn('[StoreBilling] Geri yükleme hatası:', err);
        if (platform === 'play' && isPlayBillingUnavailable(err)) return { success: false, error: PLAY_UNAVAILABLE_MSG };
        // Kullanıcı Apple/Google giriş penceresini kapattı: beklenen durum, Sentry'ye hata olarak gitmesin
        if (/cancel/i.test(String(err?.message || err))) return { success: false, error: 'Geri yükleme iptal edildi.' };
        Sentry.captureException(err, { level: opts.silent ? 'warning' : 'error', tags: { section: 'store_billing_restore', platform } });
        return { success: false, error: String(err?.message || err) };
    }
}

/**
 * iOS: uygulama açıkken StoreKit'ten gelen işlemler (Ask to Buy onayı, yenileme, başka cihazdaki alım)
 * sunucuya iletilir. Eklenti işlemi finish() eder; biz yalnızca hesaba bağlarız. Bir kez kurulur.
 */
let transactionListenerReady = false;
export function initStoreTransactionListener(): void {
    if (transactionListenerReady || getBillingPlatform() !== 'ios' || getScreenshotPlatform()) return;
    transactionListenerReady = true;
    NativePurchasesBridge.addListener('transactionUpdated', async (tx: any) => {
        if (!tx?.jwsRepresentation || !auth.currentUser) return;
        const result = await verifyOnServer({ platform: 'ios', jws: tx.jwsRepresentation }).catch((e) => ({ success: false, error: String(e) }));
        if (!result.success) Sentry.captureMessage(`[StoreBilling] transactionUpdated doğrulanamadı: ${result.error}`, 'warning');
    }).catch((e: any) => console.warn('[StoreBilling] transactionUpdated dinleyicisi kurulamadı:', e));
}

/**
 * Açılışta günde bir kez sessiz kontrol: başka cihazda/yeniden kurulumda alınmış aboneliği bağlar,
 * onaylanmamış (acknowledge) Play satın almalarını 3 gün dolmadan sunucuya iletir.
 */
export async function syncStorePurchasesSilently(): Promise<void> {
    if (getBillingPlatform() === 'web' || getScreenshotPlatform() || !auth.currentUser) return;
    initStoreTransactionListener();
    const key = `kpss_store_sync_${auth.currentUser.uid}`;
    const today = new Date().toISOString().slice(0, 10);
    try { if (localStorage.getItem(key) === today) return; } catch {}
    const result = await restorePurchases({ silent: true });
    if (result.success) console.log('[StoreBilling] Mağaza aboneliği senkronize edildi.');
    try { localStorage.setItem(key, today); } catch {}
}

/** Aboneliği yönet / iptal et — mağazanın kendi ekranı açılır. */
export async function manageSubscription(): Promise<void> {
    const platform = getBillingPlatform();
    if (platform === 'ios') {
        const { NativePurchases } = await getNativePurchases();
        await NativePurchases.manageSubscriptions();
        return;
    }
    if (platform === 'play') {
        window.location.href = `https://play.google.com/store/account/subscriptions?sku=${encodeURIComponent(MONTHLY_PRO_PRODUCT_ID)}&package=${ANDROID_PACKAGE}`;
    }
}
